import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Customer,
  DEMO_PARTNER,
  PageRequest,
  Partner,
  PartnerBoxesChartData,
  PartnerCreateUpdateRequest,
  PartnerCustomersChartData,
  PartnerStatistics,
  PartnerTopBox,
  PartnerTopCustomer,
} from '../models';
import { AuthStore, DemoStore } from '../stores';
import { Role } from '../enums';
import { DemoDataService } from './demo-data.service';
import { PartnerLightData } from '../models/partner-light-data';

@Injectable({ providedIn: 'root' })
export class PartnerService {
  private readonly httpClient = inject(HttpClient);
  private readonly authStore = inject(AuthStore);
  private readonly demoStore = inject(DemoStore);
  private readonly demoDataService = inject(DemoDataService);
  $user = this.authStore.user;

  getPartnersByRole(pageRequest: PageRequest): Observable<Partner[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartners());
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminPartners(pageRequest);
      default:
        return of([]);
    }
  }

  createPartner(request: PartnerCreateUpdateRequest): Observable<Partner> {
    if (this.demoStore.isDemoMode()) {
      return of(DEMO_PARTNER);
    }
    return this.httpClient
      .post<any>('/api/admin/partners', request)
      .pipe(map((partner) => Partner.mapFromApiValue(partner)));
  }

  updatePartner(id: string, request: PartnerCreateUpdateRequest): Observable<Partner> {
    if (this.demoStore.isDemoMode()) {
      return of(DEMO_PARTNER);
    }
    return this.httpClient
      .patch<any>(`/api/admin/partners/${id}`, request)
      .pipe(map((partner) => Partner.mapFromApiValue(partner)));
  }

  deletePartner(id: string): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    return this.httpClient.delete<void>(`/api/admin/partners/${id}`);
  }

  getAdminPartners(pageRequest: PageRequest): Observable<Partner[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartners());
    }
    return this.httpClient
      .get<any[]>('/api/admin/partners', { params: PageRequest.mapToQuery(pageRequest) })
      .pipe(map((partners) => partners.map((partner) => Partner.mapFromApiValue(partner))));
  }

  getPartners(pageRequest: PageRequest): Observable<Partner[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartners());
    }
    return this.httpClient
      .get<any[]>('/api/admin/partners', { params: PageRequest.mapToQuery(pageRequest) })
      .pipe(map((partners) => partners.map((partner) => Partner.mapFromApiValue(partner))));
  }

  getAllPartners(): Observable<PartnerLightData[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getAllPartners());
    }
    return this.httpClient.get<PartnerLightData[]>('/api/admin/partners/all');
  }

  getPartnerByRole(id: string): Observable<Partner> {
    if (this.demoStore.isDemoMode()) {
      return of(DEMO_PARTNER);
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminPartner(id);
      default:
        return of();
    }
  }

  getAdminPartner(id: string): Observable<Partner> {
    if (this.demoStore.isDemoMode()) {
      return of(DEMO_PARTNER);
    }
    return this.httpClient.get<Partner>(`/api/admin/partners/${id}`);
  }

  getPartnerByQrCodeId(qrCodeId: string): Observable<Partner> {
    if (this.demoStore.isDemoMode()) {
      return of(DEMO_PARTNER);
    }
    return this.httpClient.get<Partner>(`/api/partners/qr-code/${qrCodeId}`);
  }

  getPartnerStatistics(id?: string): Observable<PartnerStatistics> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartnerStatistics());
    }
    return this.httpClient.get<PartnerStatistics>(`/api/partners/${id || 'me'}/statistics`);
  }

  getPartnerTopCustomers(id?: string): Observable<PartnerTopCustomer[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartnerTopCustomers());
    }
    return this.httpClient.get<PartnerTopCustomer[]>(`/api/partners/${id || 'me'}/customers/top`);
  }

  getPartnerTopBoxes(id?: string): Observable<PartnerTopBox[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartnerTopBoxes());
    }
    return this.httpClient.get<PartnerTopBox[]>(`/api/partners/${id || 'me'}/boxes/top`);
  }

  getPartnerCustomers(pageRequest: PageRequest, id?: string): Observable<Customer[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getDemoCustomers());
    } else {
      return this.httpClient.get<Customer[]>(`/api/partners/${id || 'me'}/customers`, {
        params: PageRequest.mapToQuery(pageRequest),
      });
    }
  }

  getPartnerCustomersChart(id?: string): Observable<PartnerCustomersChartData> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartnerCustomersChart());
    }
    return this.httpClient.get<PartnerCustomersChartData>(`/api/partners/${id || 'me'}/customers/chart`);
  }

  getPartnerBoxesChart(id?: string): Observable<PartnerBoxesChartData> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getPartnerBoxesChart());
    }
    return this.httpClient.get<PartnerBoxesChartData>(`/api/partners/${id || 'me'}/boxes/chart`);
  }
}
